import * as Haptics from 'expo-haptics';
import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { CheckInItem } from '@/contexts/check-in-context-types';
import { createStyles } from '@/contexts/theme-context';
import { ListEmptyState } from '@/components/list-empty-state';
import { OrderCheckInSingleItemComponent } from './order-check-in-single-item-component';
import { ScanInputField } from '@/components/scanner/scan-input-field';
import { ScannerType, ScanSound } from '@/components/scanner/scanner-types';
import { useCheckInContext } from '@/contexts/check-in-context';

interface Props {
  checkInItem: CheckInItem;
}

export const OrderCheckInSingleItemScreen: React.FC<Props> = ({
  checkInItem,
}) => {
  const [scannedItem, setScannedItem] = useState(checkInItem);
  const [searchTerm, setSearchTerm] = useState('');

  const [styles] = useStyles();
  const {
    autoIncrementQuantity,
    adaptCheckInItemFragment,
    clearSearchResults,
    itemQuantityMap,
    onItemQuantityUpdate,
    playScanSound,
    searchForItems,
    searchResults,
    shippedItems,
  } = useCheckInContext();

  const shippedItem = shippedItems?.find(
    (item) => item.uuid === scannedItem.checkInItemUUID,
  );
  const shipQuantity = shippedItem?.order_invoice_line_item.ship_quantity;
  const quantity = itemQuantityMap[scannedItem.checkInItemUUID];

  const bothQuantitiesKnown =
    quantity !== undefined && shipQuantity !== undefined;

  const onSearch = async (code: string) => {
    setSearchTerm(code);
    const checkInSearchResults = searchForItems(code, ScannerType.External);

    if (checkInSearchResults.length === 1) {
      const matchedCheckInItem = adaptCheckInItemFragment(
        checkInSearchResults[0],
      );
      if (matchedCheckInItem.checkInItemUUID !== scannedItem.checkInItemUUID) {
        setScannedItem(matchedCheckInItem);
      } else {
        autoIncrementQuantity(scannedItem);
      }
      return;
    }

    if (checkInSearchResults && checkInSearchResults.length === 0) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
      playScanSound(ScanSound.NoResults);
    } else if (checkInSearchResults && checkInSearchResults.length > 1) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      playScanSound(ScanSound.MultipleResults);
    }
  };

  const onClear = () => {
    setSearchTerm('');
    clearSearchResults();
  };

  useEffect(() => {
    autoIncrementQuantity(scannedItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedItem.checkInItemUUID]);

  return (
    <>
      <ScanInputField
        containerStyle={styles.hiddenScanInput}
        onClear={onClear}
        onSearch={onSearch}
      />
      {searchResults && searchResults.length === 0 ? (
        <ListEmptyState
          text={i18n.t('no_results_for', {
            searchTerm,
          })}
        />
      ) : bothQuantitiesKnown ? (
        <OrderCheckInSingleItemComponent
          checkInItem={scannedItem}
          onItemQuantityUpdate={onItemQuantityUpdate}
          quantity={quantity}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles(() => ({
  hiddenScanInput: {
    /*
     * Initially used display: "none",
     * but that messed with functionality of the ScanInputField on Android.
     * This approach works on both platforms
     * */
    height: 0,
    opacity: 0,
  },
}));
